import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Calendar, CreditCard } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';

const DeletionWarning = () => {
  const { isTrialExpired, isSubscribed, daysUntilDeletion, scheduledDeletionDate } = useSubscription();

  // Only show warning for expired trial users without active subscription
  if (!isTrialExpired || isSubscribed || !scheduledDeletionDate || !daysUntilDeletion) {
    return null;
  }

  const isUrgent = daysUntilDeletion <= 7; // Show urgent warning if less than 7 days

  return (
    <div className={`rounded-md p-4 mb-6 ${
      isUrgent 
        ? 'bg-red-50 border-l-4 border-red-500' 
        : 'bg-orange-50 border-l-4 border-orange-500'
    }`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertTriangle 
            size={24} 
            className={isUrgent ? 'text-red-500' : 'text-orange-500'} 
          />
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${
            isUrgent ? 'text-red-800' : 'text-orange-800'
          }`}>
            {isUrgent ? 'URGENT: Account Scheduled for Deletion' : 'Account Deletion Warning'}
          </h3>
          <div className={`mt-2 text-sm ${
            isUrgent ? 'text-red-700' : 'text-orange-700'
          }`}>
            <p className="mb-2">
              Your trial has expired and your account is scheduled for deletion in{' '}
              <span className="font-semibold">
                {daysUntilDeletion} {daysUntilDeletion === 1 ? 'day' : 'days'}
              </span>
              {scheduledDeletionDate && (
                <span> on {scheduledDeletionDate.toLocaleDateString()}</span>
              )}.
            </p>
            <p className="mb-3">
              All your forms, responses, and data will be permanently deleted. 
              Your QR codes and form URLs are currently inactive.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Link
                to="/dashboard/billing"
                className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  isUrgent
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                    : 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
                }`}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Subscribe Now to Prevent Deletion
              </Link>
              <div className={`text-xs ${
                isUrgent ? 'text-red-600' : 'text-orange-600'
              } flex items-center`}>
                <Calendar className="mr-1 h-3 w-3" />
                Once you subscribe, all forms will be automatically reactivated
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeletionWarning; 